/**
 * Client-side twin of the compliance copy (BCC) validator used at send.
 * Wording and rules live in functions/src/shared/complianceBcc.js so the screens
 * and createCampaign / replyGraphMail always agree.
 */
import * as shared from '../../../functions/src/shared/complianceBcc.js';

/** '' when empty (no copy), the cleaned address when valid, null when not one valid email. */
export const normalizeBccAddress = shared.normalizeBccAddress as (raw: unknown) => string | null;

/** True when a stored country or location string resolves to United States. */
export const isUsComplianceCountry = shared.isUsComplianceCountry as (raw: unknown) => boolean;

export const COMPLIANCE_BCC_INVALID_MESSAGE = shared.COMPLIANCE_BCC_INVALID_MESSAGE as string;

export const COMPLIANCE_BCC_MISSING_MESSAGE =
  'Enter an address for the compliance copy, or untick it.';

export interface ComplianceCopyState {
  enabled: boolean;
  address: string;
  usOnly: boolean;
}

/**
 * Starting state of the "Send a hidden compliance copy" control.
 * A follow-up starts from its original campaign's address and US-only rule, then the sender's saved default.
 * Ticked only when there is an address to copy to.
 */
export function initialComplianceCopy(
  savedAddress?: string | null,
  parentAddress?: string | null,
  savedUsOnly?: boolean | null,
  parentUsOnly?: boolean | null
): ComplianceCopyState {
  const parent = normalizeBccAddress(parentAddress);
  const saved = normalizeBccAddress(savedAddress);
  const address = parent || saved || '';
  const enabled = Boolean(address);
  const resolvedUsOnly = parent
    ? typeof parentUsOnly === 'boolean'
      ? parentUsOnly
      : Boolean(savedUsOnly)
    : Boolean(savedUsOnly);
  return { enabled, address, usOnly: enabled && resolvedUsOnly };
}

export type ComplianceCopyRequest =
  | { ok: true; bccAddress: string; bccUsOnly: boolean }
  | { ok: false; message: string };

/**
 * The `bccAddress` and `bccUsOnly` to send. Always explicit: the address, or '' when unticked
 * (an absent key would let the server apply the saved default).
 */
export function complianceCopyForRequest(state: {
  enabled: boolean;
  address: string;
  usOnly?: boolean;
}): ComplianceCopyRequest {
  if (!state.enabled) return { ok: true, bccAddress: '', bccUsOnly: false };
  const clean = normalizeBccAddress(state.address);
  if (clean === null) return { ok: false, message: COMPLIANCE_BCC_INVALID_MESSAGE };
  if (!clean) return { ok: false, message: COMPLIANCE_BCC_MISSING_MESSAGE };
  return { ok: true, bccAddress: clean, bccUsOnly: Boolean(state.usOnly) };
}

/** Short Options summary text, or null when no copy will be sent. */
export function complianceCopySummary(state: {
  enabled: boolean;
  address: string;
  usOnly?: boolean;
}): string | null {
  if (!state.enabled || !normalizeBccAddress(state.address)) return null;
  return state.usOnly ? 'compliance copy on (US only)' : 'compliance copy on';
}

export interface ComplianceRecipientCounts {
  total: number;
  usCount: number;
  nonUsCount: number;
  blankCount: number;
}

/** Counts how many sendable recipients resolve to United States vs non-US vs blank country. */
export function countComplianceRecipients(
  recipients: ReadonlyArray<{ country?: string | null }>
): ComplianceRecipientCounts {
  let usCount = 0;
  let nonUsCount = 0;
  let blankCount = 0;
  for (const recipient of recipients) {
    const raw = typeof recipient.country === 'string' ? recipient.country.trim() : '';
    if (!raw) {
      blankCount += 1;
    } else if (isUsComplianceCountry(raw)) {
      usCount += 1;
    } else {
      nonUsCount += 1;
    }
  }
  return {
    total: recipients.length,
    usCount,
    nonUsCount,
    blankCount,
  };
}
